import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Cross, Menu, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/courses", label: "Courses" },
  { to: "/admissions", label: "Admissions" },
  { to: "/donate", label: "Donate" },
  { to: "/contact", label: "Contact" },
  { to: "/student-login", label: "Student" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/90 shadow-[0_1px_0_rgba(26,67,49,0.1),0_12px_40px_-20px_rgba(17,42,32,0.25)] backdrop-blur-xl" : "bg-cream/60 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-forest text-cream transition-transform duration-500 group-hover:rotate-[15deg]">
            <Cross className="h-5 w-5" />
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-xl font-bold tracking-tight text-forest-deep">St. Mary's</span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-terracotta-dark">Charitable Trust</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="nav-desktop-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative font-display text-sm font-medium tracking-wide transition-colors ${
                  isActive ? "text-forest" : "text-stone-500 hover:text-forest"
                } after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-terracotta after:transition-all after:duration-300 ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Button
            asChild
            data-testid="nav-enquire-button"
            className="hidden rounded-full bg-forest px-6 font-display text-sm text-cream hover:bg-forest-deep sm:inline-flex"
          >
            <Link to="/contact">
              Enquire Now <ArrowUpRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-menu-button" className="rounded-full border border-forest/20 p-2.5 text-forest lg:hidden" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-cream" data-testid="nav-mobile-drawer">
              <div className="mt-10 flex flex-col gap-2">
                {LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `rounded-xl px-4 py-3 font-serif text-2xl font-semibold transition-colors ${
                        isActive ? "bg-forest text-cream" : "text-forest-deep hover:bg-linen"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Button asChild className="mt-6 rounded-full bg-terracotta font-display text-white hover:bg-terracotta-dark" data-testid="nav-mobile-enquire-button">
                  <Link to="/contact">Enquire Now</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
